import React, { useMemo, useState } from "react";
import { pagosApi } from "./pagosApi";

const CATEGORY_LABELS = {
  INCLUIDO_TXT: "Incluido en el TXT · sin confirmación bancaria",
  PROPUESTO: "Propuesto para el lote · simulado",
  EXCLUIDO_PAGO_MANUAL_INFORMADO: "Excluido · pago manual informado",
  EXCLUIDO_DATOS_INVALIDOS: "Excluido · datos inválidos",
  EXCLUIDO_SIN_JUSTIFICAR: "Excluido · sin justificar",
};

const CONCILIATION_LABELS = {
  NO_EVALUADO: "No evaluada (sin corrida)",
  MAESTRO_NO_DISPONIBLE: "Maestro CRM no disponible",
  SIN_MAESTRO_CRM: "CUIT sin maestro CRM",
  SIN_OPERACIONES_EN_CORRIDA: "Sin operaciones en la corrida",
  CON_PENDIENTES: "Con pendientes",
  CONCILIADO: "Operaciones conciliadas",
};

export const FILTERS = [
  { key: "TODOS", label: "Todos" },
  { key: "APTO_REVISION", label: "Aptos para revisión" },
  { key: "PAGO_MANUAL", label: "Pago manual informado" },
  { key: "DATOS_INVALIDOS", label: "Datos inválidos" },
  { key: "DIFERENCIA_PENDIENTE", label: "Diferencias pendientes" },
];

const money = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 });
const formatMoney = (value) => (typeof value === "number" && Number.isFinite(value) ? money.format(value) : "No disponible");

export const filterGroups = (groups = [], filter = "TODOS") => (
  filter === "TODOS" ? groups : groups.filter((group) => (group.filtros || []).includes(filter))
);

const COUNT_CHECKS = new Set(["FUR_SIN_IMPORTES_AUSENTES", "TXT_TRAILER_CANTIDAD", "TXT_LONGITUD_REGISTROS", "TXT_SECUENCIA"]);
const formatCheckValue = (check, value) => (
  value == null ? "—" : COUNT_CHECKS.has(check.codigo) ? String(value) : formatMoney(value)
);

const isIncluded = (group) => group.categoria === "INCLUIDO_TXT" || group.categoria === "PROPUESTO";

const CheckMark = ({ ok }) => (
  <span className={`pagos-status ${ok === true ? "pagos-status--safe" : ok === false ? "pagos-status--warning" : ""}`}>
    {ok === true ? "Cumple" : ok === false ? "No cumple" : "No evaluable"}
  </span>
);

function ManualPaymentForm({ onSubmit, onCancel }) {
  const [informadoPor, setInformadoPor] = useState("");
  const [observacion, setObservacion] = useState("");
  return (
    <div className="pagos-manual-form">
      <label><span>Informado por</span>
        <input aria-label="Informado por" value={informadoPor} onChange={(event) => setInformadoPor(event.target.value)} />
      </label>
      <label><span>Observación</span>
        <input aria-label="Observación" value={observacion} onChange={(event) => setObservacion(event.target.value)} />
      </label>
      <small>Queda como información, no como pago confirmado. No se guarda: se pierde al recargar.</small>
      <div>
        <button type="button" className="pagos-primary" disabled={!informadoPor.trim()}
          onClick={() => onSubmit({ informadoPor: informadoPor.trim(), observacion: observacion.trim() || null })}>Guardar en esta revisión</button>
        <button type="button" className="pagos-secondary" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

const txtDateToInput = (value) => (/^\d{8}$/.test(value || "") ? `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}` : "");

function RegistrarRevision({ persistencia, defaultDate, fechaFija = false, onRegister }) {
  const [fechaPago, setFechaPago] = useState(defaultDate);
  const [estado, setEstado] = useState({ loading: false, message: "", error: "" });
  if (!persistencia?.habilitada) return null;

  const register = async () => {
    setEstado({ loading: true, message: "", error: "" });
    try {
      const response = await onRegister(fechaPago);
      setEstado({
        loading: false, error: "",
        message: response?.creado
          ? `Revisión ${response.revision} registrada en ${persistencia.baseDatos}. Nada quedó pagado ni confirmado.`
          : `Este lote ya estaba registrado (revisión ${response?.revision}); no se duplicó.`,
      });
    } catch (requestError) {
      setEstado({ loading: false, message: "", error: requestError?.message || "No se pudo registrar la revisión." });
    }
  };

  return (
    <div className="pagos-reconciliation" aria-label="Registrar revisión">
      <h3>Registrar revisión · entorno de desarrollo aislado</h3>
      <p className="pagos-cell-note">Guarda este análisis con historial y auditoría en {persistencia.baseDatos}. No genera archivos ni confirma pagos.</p>
      <div className="pagos-filter-grid">
        <label className="pagos-date"><span>Fecha de pago del lote</span>
          <input aria-label="Fecha de pago del lote" type="date" value={fechaPago} disabled={fechaFija}
            onChange={(event) => setFechaPago(event.target.value)} />
        </label>
        <button type="button" className="pagos-primary" disabled={!fechaPago || estado.loading} onClick={register}>
          {estado.loading ? "Registrando…" : "Registrar revisión"}
        </button>
      </div>
      {estado.message && <div className="pagos-alert pagos-alert--info" role="status">{estado.message}</div>}
      {estado.error && <div className="pagos-alert pagos-alert--error" role="alert">{estado.error}</div>}
    </div>
  );
}

/**
 * Resultado de los Controles previos. Lo usan el control con FUR Excel y el procesamiento SAS
 * desde una importación: la misma vista, el mismo criterio, distinta fuente.
 */
export function ResultadoControles({ result, persistencia, defaultDate, fechaFija = false, onDeclare, onUndeclare, onRegister }) {
  const [filter, setFilter] = useState("TODOS");
  const [editing, setEditing] = useState("");
  const groups = useMemo(() => filterGroups(result?.grupos || [], filter), [result, filter]);
  const counts = useMemo(() => Object.fromEntries(FILTERS.map(({ key }) => [key, filterGroups(result?.grupos || [], key).length])), [result]);
  const r = result.conciliacion;
  const summary = result.resumen;
  const withTxt = r?.base === "TXT";

  return (
    <>
        {(result.fuentes?.faltantes || []).length > 0 && (
          <div className="pagos-alert pagos-alert--warning" role="status">
            <strong>Fuentes faltantes:</strong>
            <ul>{result.fuentes.faltantes.map((item) => <li key={item}>{item}</li>)}</ul>
          </div>
        )}

        <div className="pagos-summary" aria-label="Resumen de controles previos">
          <div className="pagos-summary-card"><span>Grupos FUR</span><strong>{summary.grupos}</strong><small>Hoja {result.fuentes?.hojaFur}</small></div>
          <div className="pagos-summary-card"><span>{withTxt ? "Incluidos en el TXT" : "Propuestos (simulado)"}</span><strong>{summary.incluidos}</strong><small>{formatMoney(summary.importeIncluido)}</small></div>
          <div className="pagos-summary-card"><span>Excluidos</span><strong>{summary.excluidos}</strong><small>{formatMoney(summary.importeExcluido)}</small></div>
        </div>

        <div className="pagos-reconciliation" aria-label="Conciliación de importes">
          <h3>Conciliación de importes</h3>
          <dl>
            <div><dt>1. Total del FUR</dt><dd>{formatMoney(r.totalFur)}</dd></div>
            <div><dt>2. {withTxt ? "Incluido en el TXT" : "Lote propuesto (simulado)"}</dt><dd>{formatMoney(r.incluido)}</dd></div>
            <div><dt>3. Excluido</dt><dd>{formatMoney(r.excluido)}
              <small>Pago manual informado {formatMoney(r.excluidoPagoManualInformado)} · Datos inválidos {formatMoney(r.excluidoDatosInvalidos)}</small></dd></div>
            <div><dt>4. Pendiente de justificar</dt><dd>{formatMoney(r.pendienteJustificar)}</dd></div>
            <div><dt>5. Confirmado por banco</dt><dd>{r.confirmadoBanco == null ? "Sin evidencia bancaria" : formatMoney(r.confirmadoBanco)}</dd></div>
          </dl>
          <div className="pagos-table-wrap"><table><thead><tr><th>Verificación</th><th>Fórmula</th><th>Izquierda</th><th>Derecha</th><th>Resultado</th></tr></thead>
            <tbody>{r.verificaciones.map((check) => (
              <tr key={check.codigo}><td>{check.descripcion}{check.detalle && <small className="pagos-cell-note">{check.detalle}</small>}</td>
                <td><code>{check.formula}</code></td>
                <td>{formatCheckValue(check, check.izquierda)}</td><td>{formatCheckValue(check, check.derecha)}</td><td><CheckMark ok={check.ok} /></td></tr>
            ))}</tbody></table></div>
        </div>

        {(result.incidenciasGenerales || []).length > 0 && (
          <div className="pagos-alert pagos-alert--warning" role="status">
            <strong>Incidencias generales</strong>
            <ul>{result.incidenciasGenerales.map((issue, index) => <li key={`${issue.code}-${index}`}>{issue.message}</li>)}</ul>
          </div>
        )}

        <div className="pagos-channel pagos-filters" role="group" aria-label="Filtrar comercios"><div>
          {FILTERS.map(({ key, label }) => (
            <button key={key} type="button" className={filter === key ? "is-active" : ""} aria-pressed={filter === key}
              onClick={() => setFilter(key)}>{label} ({counts[key]})</button>
          ))}
        </div></div>

        <div className="pagos-table-wrap"><table><thead><tr>
          <th>Comercio</th><th>Cuenta</th><th>Importe</th><th>Lote</th><th>Conciliación</th><th>Validaciones</th><th>Pago manual</th>
        </tr></thead><tbody>
          {!groups.length ? <tr><td colSpan="7"><div className="pagos-empty"><p>No hay comercios para este filtro.</p></div></td></tr> : groups.map((group) => (
            <tr key={group.id} className={group.filtros.includes("DATOS_INVALIDOS") || group.filtros.includes("DIFERENCIA_PENDIENTE") ? "pagos-row--review" : ""}>
              <td><strong>{group.nombreComercio}</strong>
                <small className="pagos-cell-note">{group.razonSocial}</small>
                <small className="pagos-cell-note">CUIT {group.cuitEnmascarado} · Legajo {group.legajo} · CP {group.codigoPostal || "sin dato"} · Fila FUR {group.filaFur}</small></td>
              <td>{group.tipoCuenta || "Sin tipo"} {group.cuentaEnmascarada}
                <small className="pagos-cell-note">Registro simulado: {group.longitudRegistroSimulado ?? "error"} caracteres</small></td>
              <td>{formatMoney(group.importe)}</td>
              <td><span className={`pagos-status ${isIncluded(group) ? "pagos-status--safe" : "pagos-status--warning"}`}>{CATEGORY_LABELS[group.categoria] || group.categoria}</span>
                {group.secuenciaTxt != null && <small className="pagos-cell-note">Secuencia TXT {group.secuenciaTxt}</small>}</td>
              <td>{CONCILIATION_LABELS[group.conciliacion?.estado] || group.conciliacion?.estado}
                {group.conciliacion?.operaciones > 0 && <small className="pagos-cell-note">{group.conciliacion.operaciones} operaciones · {group.conciliacion.sinConciliar} sin conciliar</small>}</td>
              <td>{!group.validaciones.length ? <span className="pagos-status pagos-status--safe">Sin observaciones</span>
                : <ul className="pagos-issues">{group.validaciones.map((issue, index) => (
                  <li key={`${issue.code}-${index}`} className={`pagos-issue pagos-issue--${issue.severity === "ERROR" ? "error" : issue.severity === "ADVERTENCIA" ? "warning" : "info"}`}>
                    <b>{issue.severity}</b> {issue.message}</li>
                ))}</ul>}</td>
              <td>{group.pagoManual ? <>
                <span className="pagos-status pagos-status--pending">Informado por {group.pagoManual.informadoPor}</span>
                <small className="pagos-cell-note">Sin confirmación bancaria{group.pagoManual.observacion ? ` · ${group.pagoManual.observacion}` : ""}</small>
                <button type="button" className="pagos-secondary" onClick={() => onUndeclare(group.id)}>Quitar</button>
              </> : !isIncluded(group) && (editing === group.id
                ? <ManualPaymentForm onSubmit={(value) => { setEditing(""); onDeclare(group.id, value); }} onCancel={() => setEditing("")} />
                : <button type="button" className="pagos-secondary" onClick={() => setEditing(group.id)}>Informar pago manual</button>)}
              </td>
            </tr>
          ))}
        </tbody></table></div>
        <p className="pagos-cell-note">{result.layoutNote}</p>
        <RegistrarRevision key={defaultDate || "sin-fecha"} persistencia={persistencia}
          defaultDate={defaultDate} fechaFija={fechaFija} onRegister={onRegister} />
    </>
  );
}

export default function ControlesPrevios({ token, runId, runRows, persistencia, onRegistered }) {
  const [fur, setFur] = useState(null);
  const [txt, setTxt] = useState(null);
  const [result, setResult] = useState(null);
  const [declarations, setDeclarations] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const runAvailable = Boolean(runId && runRows);

  const analyze = async (nextDeclarations = declarations) => {
    if (!fur) return;
    setLoading(true); setError("");
    try {
      const pagosManuales = Object.entries(nextDeclarations).map(([grupoId, value]) => ({ grupoId, ...value }));
      const response = await pagosApi.analizarControlesPrevios({ fur, txt, runId, runRows, pagosManuales }, token);
      if (response?.mode !== "READ_ONLY_PREVIEW" || response?.persisted !== false) {
        throw new Error("El servicio no devolvió un análisis de solo lectura verificable.");
      }
      setResult(response);
    } catch (requestError) {
      setResult(null);
      setError(requestError?.message || "No se pudieron ejecutar los controles previos.");
    } finally {
      setLoading(false);
    }
  };

  const changeFile = (setter) => (event) => {
    setter(event.target.files?.[0] || null);
    setResult(null); setDeclarations({}); setError("");
  };

  const declare = (groupId, value) => {
    const next = { ...declarations, [groupId]: value };
    setDeclarations(next);
    analyze(next);
  };

  const undeclare = (groupId) => {
    const next = { ...declarations };
    delete next[groupId];
    setDeclarations(next);
    analyze(next);
  };

  const registerRevision = async (fechaPago) => {
    const pagosManuales = Object.entries(declarations).map(([grupoId, value]) => ({ grupoId, ...value }));
    const response = await pagosApi.registrarLiquidacion({ fur, txt, runId, runRows, pagosManuales, fechaPago }, token);
    if (onRegistered) onRegistered(response);
    return response;
  };

  return (
    <section className="pagos-panel" aria-labelledby="pagos-controles-title" data-testid="controles-previos">
      <div className="pagos-section-heading">
        <div>
          <h2 id="pagos-controles-title">Controles previos</h2>
          <p>Revisa el FUR antes del pago: datos bancarios, conciliación y qué comercios entran o no en el lote.</p>
        </div>
        <span className="pagos-mode-badge">Solo lectura · simulación</span>
      </div>

      <div className="pagos-alert pagos-alert--info" role="note">
        No genera archivos bancarios ni registra pagos. Ningún importe se considera pagado sin evidencia bancaria.
      </div>

      <div className="pagos-upload-grid">
        <label className="pagos-date"><span>Reporte Zoco con hoja FUR (.xlsx / .xlsm)</span>
          <input aria-label="Archivo FUR" type="file" accept=".xlsx,.xlsm" onChange={changeFile(setFur)} />
        </label>
        <label className="pagos-date"><span>TXT bancario ya generado (opcional)</span>
          <input aria-label="Archivo TXT" type="file" accept=".txt" onChange={changeFile(setTxt)} />
        </label>
        <div className="pagos-upload-note">
          <span>Corrida de conciliación</span>
          <strong>{runAvailable ? `Corrida ${runId}` : "Sin corrida seleccionada"}</strong>
          <small>{runAvailable ? "Se cruza por CUIT con el maestro CRM." : "Elegí una corrida arriba para evaluar la conciliación."}</small>
        </div>
        <button type="button" className="pagos-primary" disabled={!fur || loading} onClick={() => analyze()}>
          {loading ? "Analizando…" : "Ejecutar controles"}
        </button>
      </div>
      {error && <div className="pagos-alert pagos-alert--error" role="alert">{error}</div>}

      {result && <ResultadoControles result={result} persistencia={persistencia}
        defaultDate={txtDateToInput(result.fuentes?.fechaPagoTxt)} onDeclare={declare} onUndeclare={undeclare} onRegister={registerRevision} />}
    </section>
  );
}
